import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Aviso,
  CampoDeSelecao,
  CartaoIndicador,
  EstadoVazio,
  ROTULO_STATUS_CONTRATO,
  SeloStatusContrato,
  Tabela,
  ValorFiscal,
  formatarMoeda,
  type ColunaTabela,
  type StatusContrato,
} from '../componentes/ui';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { requisicaoAutenticada } from '../servicos/http';
import { useExercicioVigente } from '../servicos/exercicio';
import type { ColunaDaPlanilha } from '../servicos/planilha';
import { ErroDaApi } from './ErroDaApi';

interface LinhaDoRelatorio {
  readonly clienteId: string;
  readonly clienteNome: string | null;
  readonly status: string;
  readonly valorUtilizacao: number;
  readonly beneficioEconomico: number;
  readonly comissao: number;
}

interface RelatorioFornecedor {
  readonly businessId: string;
  readonly ano: number;
  readonly anoSped: number;
  /** RN-169 — no ano-alvo do assessor os números saem dos vínculos, não de contrato. */
  readonly projetado: boolean;
  readonly linhas: readonly LinhaDoRelatorio[];
  readonly geral: {
    readonly totalCalculos: number;
    readonly totalContratosFechados: number;
    readonly percentualConversaoPercentual: number;
    readonly valorUtilizacaoTotal: number;
    readonly beneficioEconomicoTotal: number;
    readonly comissaoTotal: number;
    readonly mesesAproveitamento: number;
    readonly mediaPorContratoFechadoReais: number;
    /** Só o fornecedor tem: crédito que já saiu, contra o negociado. */
    readonly valorTransferido?: number;
    readonly desagioMedioVendidoPercentual?: number;
  };
  readonly metricasIndisponiveis: readonly string[];
}

/**
 * O ano do participante em cinco números — fornecedor ou assessor.
 *
 * ## Um componente para os dois papéis
 *
 * `GET /icms/dashboard/{fornecedor,assessor}/:id/:ano` devolvem a **mesma**
 * forma, e a leitura é quase a mesma: quantos clientes, quanto de crédito,
 * quanto rendeu. O que muda é o que a receita significa — para o fornecedor é o
 * que ele recebe pelo crédito; para o assessor, o spread entre o que compra e o
 * que revende — e duas métricas que só o fornecedor tem.
 *
 * Duplicar a tela deixaria uma das duas para trás na próxima mudança, que é
 * exatamente o que aconteceu com o cadastro de cliente nesta mesma semana.
 *
 * ## Vendido e transferido não são a mesma coisa
 *
 * "Valor vendido" é o crédito negociado nos contratos do ano; "transferido" é o
 * que **já saiu**. Um fornecedor com R$ 4 milhões vendidos e R$ 1,2 milhão
 * transferido tem três quartos do ano por entregar — e essa é a diferença que o
 * relatório existe para mostrar. Um número só esconderia isso.
 *
 * ## O deságio médio é ponderado
 *
 * Média simples entre meses daria o mesmo peso a um mês de R$ 5 mil e a um de
 * R$ 5 milhões. Aqui é receita sobre crédito transferido: o deságio que a
 * carteira praticou de fato.
 */
/** Os clientes do exercício, com o contrato por extenso e os valores como número. */
function colunasDoRelatorio(ehFornecedor: boolean): ColunaDaPlanilha<LinhaDoRelatorio>[] {
  return [
    { titulo: 'Cliente', largura: 44, valor: (l) => l.clienteNome ?? l.clienteId.slice(0, 8) },
    {
      titulo: 'Contrato',
      largura: 22,
      valor: (l) => ROTULO_STATUS_CONTRATO[l.status as StatusContrato] ?? l.status,
    },
    { titulo: 'Crédito vendido', formato: 'moeda', valor: (l) => l.valorUtilizacao },
    { titulo: 'Benefício ao cliente', formato: 'moeda', valor: (l) => l.beneficioEconomico },
    {
      titulo: ehFornecedor ? 'Receita do fornecedor' : 'Spread do assessor',
      formato: 'moeda',
      // Negativo é "sem dado" na tela; na planilha, célula vazia.
      valor: (l) => (l.comissao < 0 ? null : l.comissao),
    },
  ];
}

export function RelatorioDoParticipante(props: {
  readonly businessId: string;
  readonly papel: 'fornecedor' | 'assessor';
}): JSX.Element {
  const anoAtual = useExercicioVigente(); // RN-620
  const [ano, setAno] = useState(anoAtual);
  const ehFornecedor = props.papel === 'fornecedor';

  const relatorio = useQuery({
    queryKey: ['relatorio-participante', props.papel, props.businessId, ano],
    queryFn: () =>
      requisicaoAutenticada<RelatorioFornecedor>(
        `/icms/dashboard/${props.papel}/${props.businessId}/${ano}`,
      ),
  });

  const colunas: readonly ColunaTabela<LinhaDoRelatorio>[] = [
    {
      chave: 'clienteNome',
      titulo: 'Cliente',
      larguraMinima: '22rem',
      celula: (l) => (
        <Link
          to={`/clientes/${l.clienteId}`}
          className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          {l.clienteNome ?? l.clienteId.slice(0, 8)}
        </Link>
      ),
    },
    {
      chave: 'status',
      titulo: 'Contrato',
      larguraMinima: '10rem',
      celula: (l) => <SeloStatusContrato status={l.status as StatusContrato} />,
    },
    {
      chave: 'valorUtilizacao',
      titulo: 'Crédito vendido',
      numerica: true,
      celula: (l) => <ValorFiscal valor={l.valorUtilizacao} />,
    },
    {
      chave: 'beneficioEconomico',
      titulo: 'Benefício ao cliente',
      numerica: true,
      celula: (l) => <ValorFiscal valor={l.beneficioEconomico} variante="economia" />,
    },
    {
      chave: 'comissao',
      // Para o fornecedor é o que ele recebe; para o assessor, o spread entre o
      // que compra e o que revende. Mesmo campo, negócio diferente — e o rótulo
      // precisa dizer qual dos dois.
      titulo: ehFornecedor ? 'Receita do fornecedor' : 'Spread do assessor',
      numerica: true,
      celula: (l) =>
        l.comissao < 0 ? (
          <span className="text-xs text-conteudo-sutil">Sem dado</span>
        ) : (
          <ValorFiscal valor={l.comissao} />
        ),
    },
  ];

  if (relatorio.isError) {
    return <ErroDaApi titulo="Não foi possível carregar o relatório" erro={relatorio.error} />;
  }

  const dados = relatorio.data;
  const geral = dados?.geral;
  const semSpread = (geral?.comissaoTotal ?? 0) < 0;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">Relatório anual</h2>
          <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
            {ehFornecedor
              ? 'O ano deste fornecedor: quanto vendeu, quanto já transferiu e a que deságio.'
              : 'O ano deste assessor: quantos clientes, quanto de crédito e quanto rendeu de spread.'}
            {dados !== undefined && ` Apurado sobre o SPED de ${dados.anoSped}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <CampoDeSelecao
            rotulo="Exercício"
            className="w-36"
            value={String(ano)}
            onChange={(e) => setAno(Number(e.target.value))}
            opcoes={[anoAtual + 1, anoAtual, anoAtual - 1, anoAtual - 2].map((a) => ({
              valor: String(a),
              rotulo: String(a),
            }))}
          />
          <BotaoExtrairXlsx
            titulo={`Relatório ${ehFornecedor ? 'do fornecedor' : 'do assessor'} ${ano}`}
            disabled={dados === undefined}
            colunas={colunasDoRelatorio(ehFornecedor)}
            linhas={dados?.linhas ?? []}
          />
        </div>
      </div>

      {dados?.projetado === true && (
        <Aviso tom="informacao" titulo={`${ano} é projeção, não realizado`}>
          Ainda quase não há contrato fechado neste exercício. Os números saem dos vínculos de
          cadastro (RN-169): é o que a carteira renderia mantendo o que está combinado hoje.
        </Aviso>
      )}

      <div
        className={`grid gap-4 sm:grid-cols-2 ${ehFornecedor ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}
      >
        <CartaoIndicador
          rotulo="Clientes"
          valor={geral?.totalCalculos ?? 0}
          detalhe={`${geral?.totalContratosFechados ?? 0} com contrato fechado`}
        />
        <CartaoIndicador
          rotulo={ehFornecedor ? 'Valor vendido' : 'Crédito intermediado'}
          valor={formatarMoeda(geral?.valorUtilizacaoTotal ?? 0)}
          detalhe="Crédito negociado no ano"
        />

        {/* As duas métricas que só existem do lado do fornecedor. */}
        {ehFornecedor && (
          <>
            <CartaoIndicador
              rotulo="Valor transferido"
              valor={formatarMoeda(geral?.valorTransferido ?? 0)}
              detalhe="O que já saiu de fato"
            />
            <CartaoIndicador
              rotulo="Deságio médio vendido"
              valor={`${(geral?.desagioMedioVendidoPercentual ?? 0).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}%`}
              detalhe="Ponderado pelo volume"
            />
          </>
        )}

        {/*
          Spread negativo não é prejuízo: é dado faltando.

          O ganho é a diferença entre o que o cliente paga e o que o fornecedor
          cobra (RN-170). Sem o deságio do fornecedor no contrato, a conta vira o
          valor do cliente com o sinal trocado — um número grande, negativo e
          convincente, apoiado num zero que ninguém informou. Na base migrada
          isso vale para os 895 contratos: `desagio_venda_fornecedor` é 0 em
          todos, e o assessor da operadora aparece com R$ -2,7 milhões.

          O painel da empresa já tratava assim; esta tela repetiria o erro.
        */}
        {!ehFornecedor && (
          <CartaoIndicador
            rotulo="Spread do ano"
            valor={
              semSpread ? (
                <span className="text-2xl text-conteudo-sutil">Sem dado</span>
              ) : (
                formatarMoeda(geral?.comissaoTotal ?? 0)
              )
            }
            detalhe={
              semSpread
                ? 'O deságio do fornecedor não está preenchido nos contratos deste ano.'
                : `Média de ${formatarMoeda(geral?.mediaPorContratoFechadoReais ?? 0)} por contrato fechado`
            }
          />
        )}

        <CartaoIndicador
          rotulo="Meses de aproveitamento"
          valor={`${geral?.mesesAproveitamento ?? 0}/12`}
          detalhe="Competências com SISCRED enviado"
        />
      </div>

      {(dados?.metricasIndisponiveis.length ?? 0) > 0 && (
        <p className="text-xs text-conteudo-sutil">
          Sem dado para: {dados?.metricasIndisponiveis.join(', ')}. Aparecem como zero, não como
          número inventado.
        </p>
      )}

      {!relatorio.isPending && (dados?.linhas.length ?? 0) === 0 ? (
        <EstadoVazio
          titulo={`Nenhuma operação em ${ano}`}
          descricao={
            ehFornecedor
              ? 'Este fornecedor não tem contrato de cliente neste exercício.'
              : 'Este assessor não intermedia nenhum contrato neste exercício.'
          }
        />
      ) : (
        <Tabela
          legenda={`Clientes em ${ano}`}
          colunas={colunas}
          dados={dados?.linhas ?? []}
          chaveDaLinha={(l) => l.clienteId}
          chaveDeCabecalhoDaLinha="clienteNome"
          carregando={relatorio.isPending}
        />
      )}

      {ehFornecedor && <AssessoresNoExercicio fornecedorId={props.businessId} ano={ano} />}
    </section>
  );
}

/* ─── Assessores e clientes do fornecedor ─────────────────────────────────── */

interface AssessorNoAno {
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly nomeFantasia: string | null;
  readonly cnpj: string;
}

/**
 * RN-128 — Quem intermediou crédito deste fornecedor no exercício.
 *
 * ## Seção do relatório, não aba
 *
 * Era uma aba própria, alimentada pelos vínculos de deságio — que não têm ano:
 * listava quem *pode* comprar deste fornecedor, não quem *operou*. Aqui a fonte
 * é `GET /icms/advisors/:fornecedorId/:ano` (assessores com contrato ativo do
 * fornecedor no ano), seguindo o mesmo seletor de exercício do relatório. A
 * pergunta "quem vendeu o meu crédito em 2025" tem resposta na mesma tela em
 * que se lê quanto foi vendido.
 *
 * O deságio de compra de cada assessor continua na configuração de Crédito ICMS
 * do assessor — é vínculo de cadastro, não dado do exercício.
 */
function AssessoresNoExercicio(props: {
  readonly fornecedorId: string;
  readonly ano: number;
}): JSX.Element {
  const assessores = useQuery({
    queryKey: ['assessores-do-fornecedor', props.fornecedorId, props.ano],
    queryFn: () =>
      requisicaoAutenticada<AssessorNoAno[]>(`/icms/advisors/${props.fornecedorId}/${props.ano}`),
  });

  const colunas: readonly ColunaTabela<AssessorNoAno>[] = [
    {
      chave: 'razaoSocial',
      titulo: 'Assessor',
      larguraMinima: '24rem',
      celula: (a) => (
        <Link
          to={`/empresas/${a.businessId}`}
          className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          <span className="flex flex-col">
            <span>{a.razaoSocial}</span>
            {a.nomeFantasia !== null && a.nomeFantasia !== a.razaoSocial && (
              <span className="text-xs text-conteudo-sutil">{a.nomeFantasia}</span>
            )}
          </span>
        </Link>
      ),
    },
    {
      chave: 'cnpj',
      titulo: 'CNPJ',
      larguraMinima: '12rem',
      celula: (a) => <span className="valor-fiscal text-sm">{formatarCnpjSimples(a.cnpj)}</span>,
    },
  ];

  const lista = assessores.data ?? [];

  return (
    <section
      className="flex flex-col gap-4 border-t border-borda pt-6"
      aria-labelledby="assessores-exercicio"
    >
      <div>
        <h2 id="assessores-exercicio" className="font-display text-lg font-semibold tracking-tight">
          Assessores no exercício
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
          Quem intermediou crédito deste fornecedor em {props.ano} — assessores com contrato ativo
          no exercício. A que deságio cada um compra está na configuração de Crédito ICMS do
          assessor.
        </p>
      </div>

      {assessores.isError && (
        <ErroDaApi titulo="Não foi possível carregar os assessores" erro={assessores.error} />
      )}

      {!assessores.isPending && !assessores.isError && lista.length === 0 ? (
        <EstadoVazio
          titulo={`Nenhum assessor operou com este fornecedor em ${props.ano}`}
          descricao="Nenhum contrato ativo do exercício tem assessor vinculado a este fornecedor."
        />
      ) : (
        !assessores.isError && (
          <Tabela
            legenda={`Assessores do fornecedor em ${props.ano}`}
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(a) => a.businessId}
            chaveDeCabecalhoDaLinha="razaoSocial"
            carregando={assessores.isPending}
          />
        )
      )}
    </section>
  );
}

interface ClienteDoFornecedor {
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly cnpj: string;
  readonly statusContrato?: string | null;
  readonly anoContrato?: number | null;
  readonly assessor?: string | null;
  readonly fornecedor?: string | null;
}

/**
 * Os clientes com contrato deste participante.
 *
 * Vem de `GET /icms/clients/ano/:ano` filtrada pelo fornecedor **ou** pelo
 * assessor — não de uma consulta nova. "Quem são meus clientes" é a mesma
 * pergunta da carteira, com um filtro a mais.
 */
export function ClientesDoParticipante(props: {
  readonly businessId: string;
  readonly papel: 'fornecedor' | 'assessor';
}): JSX.Element {
  const anoAtual = useExercicioVigente(); // RN-620
  const [ano, setAno] = useState(anoAtual);

  // A mesma listagem da carteira, filtrada pelo lado que interessa — a rota já
  // aceita os dois filtros.
  const filtro =
    props.papel === 'fornecedor'
      ? `fornecedorId=${props.businessId}`
      : `parceiroId=${props.businessId}`;

  const clientes = useQuery({
    queryKey: ['clientes-do-participante', props.papel, props.businessId, ano],
    queryFn: () =>
      requisicaoAutenticada<ClienteDoFornecedor[]>(`/icms/clients/ano/${ano}?${filtro}`),
  });

  const colunas: readonly ColunaTabela<ClienteDoFornecedor>[] = [
    {
      chave: 'razaoSocial',
      titulo: 'Cliente',
      larguraMinima: '24rem',
      celula: (c) => (
        <Link
          to={`/clientes/${c.businessId}`}
          className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          {c.razaoSocial}
        </Link>
      ),
    },
    {
      chave: 'cnpj',
      titulo: 'CNPJ',
      larguraMinima: '12rem',
      celula: (c) => <span className="valor-fiscal text-sm">{formatarCnpjSimples(c.cnpj)}</span>,
    },
    {
      chave: 'assessor',
      // Do lado do assessor, a coluna útil é o fornecedor: o assessor é ele
      // mesmo em todas as linhas.
      titulo: props.papel === 'fornecedor' ? 'Assessor' : 'Fornecedor',
      larguraMinima: '16rem',
      celula: (c) =>
        (props.papel === 'fornecedor' ? c.assessor : c.fornecedor) ?? (
          <span className="text-conteudo-sutil">—</span>
        ),
    },
    {
      chave: 'statusContrato',
      titulo: 'Contrato',
      larguraMinima: '11rem',
      celula: (c) =>
        c.statusContrato == null ? (
          <span className="text-xs text-conteudo-sutil">Sem contrato</span>
        ) : (
          <SeloStatusContrato status={c.statusContrato as StatusContrato} />
        ),
    },
  ];

  const lista = clientes.data ?? [];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">Clientes</h2>
          <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
            {props.papel === 'fornecedor'
              ? 'Contribuintes com contrato deste fornecedor no exercício.'
              : 'Contribuintes que este assessor intermedia no exercício.'}
          </p>
        </div>
        <CampoDeSelecao
          rotulo="Exercício"
          className="w-36"
          value={String(ano)}
          onChange={(e) => setAno(Number(e.target.value))}
          opcoes={[anoAtual + 1, anoAtual, anoAtual - 1, anoAtual - 2].map((a) => ({
            valor: String(a),
            rotulo: String(a),
          }))}
        />
      </div>

      {clientes.isError && <ErroDaApi titulo="Não foi possível carregar" erro={clientes.error} />}

      {!clientes.isPending && lista.length === 0 ? (
        <EstadoVazio
          titulo={`Nenhum cliente em ${ano}`}
          descricao={
            props.papel === 'fornecedor'
              ? 'Nenhum contribuinte tem contrato com este fornecedor neste exercício.'
              : 'Este assessor não intermedia nenhum contrato neste exercício.'
          }
        />
      ) : (
        <Tabela
          legenda={`Clientes com contrato em ${ano}`}
          colunas={colunas}
          dados={lista}
          chaveDaLinha={(c) => c.businessId}
          chaveDeCabecalhoDaLinha="razaoSocial"
          carregando={clientes.isPending}
        />
      )}
    </section>
  );
}

/** `12345678000199` → `12.345.678/0001-99`. */
function formatarCnpjSimples(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
